import { reverse, sortBy } from 'lodash';

import { addMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { denormalisedResponseEntities } from '../../util/data';
import { storableError } from '../../util/errors';
import { createImageVariantConfig, types as sdkTypes } from '../../util/sdkLoader';
import { getInquiryTransitionsForQuery, isInquiryTransaction } from '../../util/transactionInquiry';
import { parse } from '../../util/urlHelpers';

const { UUID } = sdkTypes;

const MESSAGES_PAGE_SIZE = 100;
const INBOX_PAGE_SIZE = 10;

const TRANSACTIONS_LIST_INCLUDES = [
  'listing',
  'provider',
  'provider.profileImage',
  'customer',
  'customer.profileImage',
  'booking',
];

const TRANSACTIONS_LIST_FIELDS = {
  'fields.transaction': [
    'processName',
    'lastTransition',
    'lastTransitionedAt',
    'transitions',
    'payinTotal',
    'payoutTotal',
    'lineItems',
    'protectedData.serviceOfferParams',
  ],
  'fields.listing': ['title', 'description', 'availabilityPlan', 'publicData.listingType'],
  'fields.user': ['profile.displayName', 'profile.abbreviatedName'],
  'fields.image': ['variants.square-small', 'variants.square-small2x'],
};

const createTransactionsQueryParams = ({ only, page, lastTransitions }) => ({
  only,
  lastTransitions,
  include: TRANSACTIONS_LIST_INCLUDES,
  ...TRANSACTIONS_LIST_FIELDS,
  page,
  perPage: INBOX_PAGE_SIZE,
});

const sortedTransactions = txs =>
  reverse(
    sortBy(txs, tx => {
      return tx?.attributes?.lastTransitionedAt || null;
    })
  );

const mergeTransactionsById = transactions => {
  const unique = new Map();
  transactions.forEach(tx => {
    const key = tx?.id?.uuid || tx?.id;
    if (key && !unique.has(key)) {
      unique.set(key, tx);
    }
  });
  return Array.from(unique.values());
};

const entityRefs = entities =>
  entities.map(entity => ({
    id: entity.id,
    type: entity.type,
  }));

const transactionIdToKey = txId => {
  if (!txId) {
    return null;
  }
  return txId?.uuid || (typeof txId === 'string' ? txId : String(txId));
};

const getImageVariants = listingImageConfig => {
  const { aspectWidth = 1, aspectHeight = 1, variantPrefix = 'listing-card' } = listingImageConfig;
  const aspectRatio = aspectHeight / aspectWidth;
  return {
    'fields.image': [
      'variants.square-small',
      'variants.square-small2x',
      `variants.${variantPrefix}`,
      `variants.${variantPrefix}-2x`,
    ],
    ...createImageVariantConfig(`${variantPrefix}`, 400, aspectRatio),
    ...createImageVariantConfig(`${variantPrefix}-2x`, 800, aspectRatio),
  };
};

export const ActionTypes = {
  FETCH_TRANSACTIONS_REQUEST: 'app/MessagesPage/FETCH_TRANSACTIONS_REQUEST',
  FETCH_TRANSACTIONS_SUCCESS: 'app/MessagesPage/FETCH_TRANSACTIONS_SUCCESS',
  FETCH_TRANSACTIONS_ERROR: 'app/MessagesPage/FETCH_TRANSACTIONS_ERROR',

  FETCH_TRANSACTION_REQUEST: 'app/MessagesPage/FETCH_TRANSACTION_REQUEST',
  FETCH_TRANSACTION_SUCCESS: 'app/MessagesPage/FETCH_TRANSACTION_SUCCESS',
  FETCH_TRANSACTION_ERROR: 'app/MessagesPage/FETCH_TRANSACTION_ERROR',

  FETCH_MESSAGES_REQUEST: 'app/MessagesPage/FETCH_MESSAGES_REQUEST',
  FETCH_MESSAGES_SUCCESS: 'app/MessagesPage/FETCH_MESSAGES_SUCCESS',
  FETCH_MESSAGES_ERROR: 'app/MessagesPage/FETCH_MESSAGES_ERROR',

  SEND_MESSAGE_REQUEST: 'app/MessagesPage/SEND_MESSAGE_REQUEST',
  SEND_MESSAGE_SUCCESS: 'app/MessagesPage/SEND_MESSAGE_SUCCESS',
  SEND_MESSAGE_ERROR: 'app/MessagesPage/SEND_MESSAGE_ERROR',
};

const initialState = {
  fetchInProgress: false,
  fetchOrdersOrSalesError: null,
  transactionRefs: [],
  pagination: null,

  fetchTransactionInProgress: false,
  fetchTransactionError: null,
  transactionRef: null,

  fetchMessagesInProgress: false,
  fetchMessagesError: null,
  totalMessages: 0,
  totalMessagePages: 0,
  oldestMessagePageFetched: 0,
  activeMessagesTransactionId: null,
  messages: [],

  sendMessageInProgress: false,
  sendMessageError: null,
};

const mergeEntityArrays = (a, b) => {
  return a.filter(aEntity => !b.find(bEntity => aEntity.id.uuid === bEntity.id.uuid)).concat(b);
};

export default function reducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case ActionTypes.FETCH_TRANSACTIONS_REQUEST:
      return {
        ...state,
        fetchInProgress: true,
        fetchOrdersOrSalesError: null,
        transactionRefs: [],
        pagination: null,
      };
    case ActionTypes.FETCH_TRANSACTIONS_SUCCESS:
      return {
        ...state,
        fetchInProgress: false,
        fetchOrdersOrSalesError: null,
        transactionRefs: payload.transactionRefs,
        pagination: payload.pagination,
      };
    case ActionTypes.FETCH_TRANSACTIONS_ERROR:
      return {
        ...state,
        fetchInProgress: false,
        fetchOrdersOrSalesError: payload,
        transactionRefs: [],
        pagination: null,
      };

    case ActionTypes.FETCH_TRANSACTION_REQUEST:
      return {
        ...state,
        fetchTransactionInProgress: true,
        fetchTransactionError: null,
      };
    case ActionTypes.FETCH_TRANSACTION_SUCCESS:
      return {
        ...state,
        fetchTransactionInProgress: false,
        fetchTransactionError: null,
        transactionRef: payload,
      };
    case ActionTypes.FETCH_TRANSACTION_ERROR:
      return {
        ...state,
        fetchTransactionInProgress: false,
        fetchTransactionError: payload,
      };

    case ActionTypes.FETCH_MESSAGES_REQUEST: {
      const requestedTxId = payload?.txId || null;
      const shouldResetMessages =
        requestedTxId &&
        state.activeMessagesTransactionId &&
        state.activeMessagesTransactionId !== requestedTxId;

      return {
        ...state,
        fetchMessagesInProgress: true,
        fetchMessagesError: null,
        activeMessagesTransactionId: requestedTxId || state.activeMessagesTransactionId,
        ...(shouldResetMessages
          ? {
              messages: [],
              totalMessages: 0,
              totalMessagePages: 0,
              oldestMessagePageFetched: 0,
            }
          : {}),
      };
    }
    case ActionTypes.FETCH_MESSAGES_SUCCESS: {
      if (
        payload?.txId &&
        state.activeMessagesTransactionId &&
        payload.txId !== state.activeMessagesTransactionId
      ) {
        return state;
      }

      const oldestMessagePageFetched =
        state.oldestMessagePageFetched > payload.page
          ? state.oldestMessagePageFetched
          : payload.page;
      return {
        ...state,
        fetchMessagesInProgress: false,
        activeMessagesTransactionId: payload?.txId || state.activeMessagesTransactionId,
        messages: mergeEntityArrays(state.messages, payload.messages),
        totalMessages: payload.totalItems,
        totalMessagePages: payload.totalPages,
        oldestMessagePageFetched,
      };
    }
    case ActionTypes.FETCH_MESSAGES_ERROR:
      if (
        payload?.txId &&
        state.activeMessagesTransactionId &&
        payload.txId !== state.activeMessagesTransactionId
      ) {
        return state;
      }
      return {
        ...state,
        fetchMessagesInProgress: false,
        fetchMessagesError: payload.error,
      };

    case ActionTypes.SEND_MESSAGE_REQUEST:
      return { ...state, sendMessageInProgress: true, sendMessageError: null };
    case ActionTypes.SEND_MESSAGE_SUCCESS:
      return { ...state, sendMessageInProgress: false, sendMessageError: null };
    case ActionTypes.SEND_MESSAGE_ERROR:
      return { ...state, sendMessageInProgress: false, sendMessageError: payload };

    default:
      return state;
  }
}

const fetchTransactionsRequest = () => ({
  type: ActionTypes.FETCH_TRANSACTIONS_REQUEST,
});
const fetchTransactionsSuccess = payload => ({
  type: ActionTypes.FETCH_TRANSACTIONS_SUCCESS,
  payload,
});
const fetchTransactionsError = e => ({
  type: ActionTypes.FETCH_TRANSACTIONS_ERROR,
  error: true,
  payload: e,
});

const fetchTransactionRequest = () => ({ type: ActionTypes.FETCH_TRANSACTION_REQUEST });
const fetchTransactionSuccess = transactionRef => ({
  type: ActionTypes.FETCH_TRANSACTION_SUCCESS,
  payload: transactionRef,
});
const fetchTransactionError = e => ({
  type: ActionTypes.FETCH_TRANSACTION_ERROR,
  error: true,
  payload: e,
});

const fetchMessagesRequest = txId => ({
  type: ActionTypes.FETCH_MESSAGES_REQUEST,
  payload: { txId },
});
const fetchMessagesSuccess = (messages, pagination, txId) => ({
  type: ActionTypes.FETCH_MESSAGES_SUCCESS,
  payload: { messages, ...pagination, txId },
});
const fetchMessagesError = (e, txId) => ({
  type: ActionTypes.FETCH_MESSAGES_ERROR,
  error: true,
  payload: { error: e, txId },
});

const sendMessageRequest = () => ({ type: ActionTypes.SEND_MESSAGE_REQUEST });
const sendMessageSuccess = () => ({ type: ActionTypes.SEND_MESSAGE_SUCCESS });
const sendMessageError = e => ({
  type: ActionTypes.SEND_MESSAGE_ERROR,
  error: true,
  payload: e,
});

const fetchInquiryTransactions = ({ page = 1 }) => async (dispatch, getState, sdk) => {
  dispatch(fetchTransactionsRequest());

  try {
    const inquiryTransitions = getInquiryTransitionsForQuery();

    const orderQuery = sdk.transactions.query(
      createTransactionsQueryParams({
        only: 'order',
        page,
        lastTransitions: inquiryTransitions,
      })
    );
    const saleQuery = sdk.transactions.query(
      createTransactionsQueryParams({
        only: 'sale',
        page,
        lastTransitions: inquiryTransitions,
      })
    );

    const [ordersResponse, salesResponse] = await Promise.all([orderQuery, saleQuery]);
    dispatch(addMarketplaceEntities(ordersResponse));
    dispatch(addMarketplaceEntities(salesResponse));

    const ordersMeta = ordersResponse?.data?.meta || {};
    const salesMeta = salesResponse?.data?.meta || {};
    const mergedData = sortedTransactions(
      mergeTransactionsById([
        ...(ordersResponse?.data?.data || []),
        ...(salesResponse?.data?.data || []),
      ])
    );

    const mergedMeta = {
      page,
      perPage: INBOX_PAGE_SIZE * 2,
      totalItems: (ordersMeta.totalItems || 0) + (salesMeta.totalItems || 0),
      totalPages: Math.max(ordersMeta.totalPages || 1, salesMeta.totalPages || 1),
    };

    dispatch(
      fetchTransactionsSuccess({
        transactionRefs: entityRefs(mergedData),
        pagination: mergedMeta,
      })
    );
    return { data: mergedData, meta: mergedMeta };
  } catch (e) {
    dispatch(fetchTransactionsError(storableError(e)));
    throw e;
  }
};

const fetchTransaction = (id, config) => async (dispatch, getState, sdk) => {
  dispatch(fetchTransactionRequest());

  try {
    const response = await sdk.transactions.show(
      {
        id,
        include: [
          'customer',
          'customer.profileImage',
          'provider',
          'provider.profileImage',
          'listing',
          'booking',
        ],
        ...getImageVariants(config.layout.listingImage),
      },
      { expand: true }
    );

    dispatch(addMarketplaceEntities(response));

    const txData = response?.data?.data;
    const isInquiry = txData ? isInquiryTransaction(txData) : false;
    const transactionRef = isInquiry && txData?.id ? { id: txData.id, type: 'transaction' } : null;

    dispatch(fetchTransactionSuccess(transactionRef));
    return transactionRef;
  } catch (e) {
    dispatch(fetchTransactionError(storableError(e)));
    throw e;
  }
};

const fetchMessages = (txId, page, config) => (dispatch, getState, sdk) => {
  const txIdKey = transactionIdToKey(txId);
  const paging = { page, perPage: MESSAGES_PAGE_SIZE };
  dispatch(fetchMessagesRequest(txIdKey));

  return sdk.messages
    .query({
      transaction_id: txId,
      include: ['sender', 'sender.profileImage'],
      ...getImageVariants(config.layout.listingImage),
      ...paging,
    })
    .then(response => {
      const messages = denormalisedResponseEntities(response);
      const { totalItems, totalPages, page: fetchedPage } = response.data.meta;
      const pagination = { totalItems, totalPages, page: fetchedPage };
      const totalMessages = getState().MessagesPage.totalMessages;

      dispatch(fetchMessagesSuccess(messages, pagination, txIdKey));

      if (totalItems > totalMessages && page > 1) {
        dispatch(fetchMessages(txId, 1, config))
          .then(() => {})
          .catch(() => {});
      }
    })
    .catch(e => {
      dispatch(fetchMessagesError(storableError(e), txIdKey));
      throw e;
    });
};

export const sendMessage = (txId, message, config) => (dispatch, getState, sdk) => {
  dispatch(sendMessageRequest());

  return sdk.messages
    .send({ transactionId: txId, content: message })
    .then(response => {
      const messageId = response.data.data.id;
      return dispatch(fetchMessages(txId, 1, config))
        .then(() => {
          dispatch(sendMessageSuccess());
          return messageId;
        })
        .catch(() => {
          dispatch(sendMessageSuccess());
          return messageId;
        });
    })
    .catch(e => {
      dispatch(sendMessageError(storableError(e)));
      throw e;
    });
};

export const loadData = (params, search, config) => (dispatch, getState, sdk) => {
  const { page = 1 } = parse(search);
  const txIdParam = params?.id;

  const listPromise = dispatch(fetchInquiryTransactions({ page }));

  if (!txIdParam) {
    return listPromise;
  }

  let txId;
  try {
    txId = new UUID(txIdParam);
  } catch (e) {
    return Promise.reject(e);
  }

  const transactionPromise = dispatch(fetchTransaction(txId, config));

  return Promise.all([listPromise, transactionPromise]).then(([listRes, txRes]) => {
    if (!txRes) {
      return { listRes, txRes, msgRes: null };
    }

    return dispatch(fetchMessages(txId, 1, config)).then(msgRes => ({ listRes, txRes, msgRes }));
  });
};
